import React, { useState } from 'react';
import { Menu, Button, Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './Main.scss';
import { ReactSVG } from 'react-svg';
import LanguageSwitcher from '../components/LanguageSwitcher';

const MainMenu = ({ user, avatarUrl }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation('menu');

  const role = user?.role?.toLowerCase();
  let selectedKey = pathname;

  if (selectedKey?.includes('route-plans')) {
    selectedKey = '/route-plans';
  } else if (selectedKey?.includes('/users')) {
    selectedKey = '/users';
  } else if (selectedKey?.includes('stats')) {
    selectedKey = '/stats';
  } else if (selectedKey?.includes('partners')) {
    selectedKey = '/partners';
  } else if (selectedKey?.includes('routes')) {
    selectedKey = '/routes';
  } else if (selectedKey?.includes('orders')) {
    selectedKey = '/orders';
  } else if (selectedKey?.includes('hubs')) {
    selectedKey = '/hubs';
  } else if (selectedKey?.includes('fleet')) {
    selectedKey = '/fleet';
  } else if (selectedKey?.includes('drivers')) {
    selectedKey = '/drivers';
  } else if (selectedKey?.includes('scheduler')) {
    selectedKey = '/scheduler';
  }

  const items = [
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/route-plans.svg" />
          <Link to={`/admin/route-plans`}>{t('routePlan')}</Link>
        </div>
      ),
      key: '/route-plans',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/routes.svg" />
          <Link to="/admin/routes">{t('routes')}</Link>
        </div>
      ),
      key: '/routes',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/all_inbox.svg" />
          <Link to="/admin/orders">{t('orders')}</Link>
        </div>
      ),
      key: '/orders',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/all_inbox.svg" />
          <Link to="/admin/hubs">{t('hubs')}</Link>
        </div>
      ),
      key: '/hubs',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/directions_car.svg" />
          <Link to="/admin/fleet">{t('fleet')}</Link>
        </div>
      ),
      key: '/fleet',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/directions_bike.svg" />
          <Link to="/admin/drivers">{t('drivers')}</Link>
        </div>
      ),
      key: '/drivers',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/event_note.svg" />
          <Link to="/admin/scheduler">{t('scheduler')}</Link>
        </div>
      ),
      key: '/scheduler',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/all_inbox.svg" />
          <Link to="/admin/partners">{t('partners')}</Link>
        </div>
      ),
      key: '/partners',
    },
    {
      label: (
        <div className="MenuItemContent">
          <ReactSVG className="icon" src="/icons/all_inbox.svg" />
          <Link to="/admin/stats">{t('stats')}</Link>
        </div>
      ),
      key: '/stats',
    },
    ...(role === 'superadmin'
      ? [
          {
            label: (
              <div className="MenuItemContent">
                <ReactSVG className="icon" src="/icons/supervised_user_circle.svg" />
                <Link to="/admin/users">{t('users')}</Link>
              </div>
            ),
            key: '/users',
          },
        ]
      : []),
  ];

  const drawerItems = [
    { label: t('routePlan'), path: '/admin/route-plans', key: '/route-plans', icon: '/icons/route-plans.svg' },
    { label: t('routes'), path: '/admin/routes', key: '/routes', icon: '/icons/routes.svg' },
    { label: t('orders'), path: '/admin/orders', key: '/orders', icon: '/icons/all_inbox.svg' },
    { label: t('hubs'), path: '/admin/hubs', key: '/hubs', icon: '/icons/all_inbox.svg' },
    { label: t('fleet'), path: '/admin/fleet', key: '/fleet', icon: '/icons/directions_car.svg' },
    { label: t('drivers'), path: '/admin/drivers', key: '/drivers', icon: '/icons/directions_bike.svg' },
    { label: t('scheduler'), path: '/admin/scheduler', key: '/scheduler', icon: '/icons/event_note.svg' },
    { label: t('partners'), path: '/admin/partners', key: '/partners', icon: '/icons/all_inbox.svg' },
    { label: t('stats'), path: '/admin/stats', key: '/stats', icon: '/icons/all_inbox.svg' },
    ...(role === 'superadmin'
      ? [{ label: t('users'), path: '/admin/users', key: '/users', icon: '/icons/supervised_user_circle.svg' }]
      : []),
  ];

  const handleDrawerNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const { t: tc } = useTranslation('common');

  return (
    <>
      <Menu className="MainMenu desktop-menu" selectedKeys={[selectedKey]} mode="horizontal" items={items} />

      <Button className="mobile-menu-trigger" type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} />

      <Drawer
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        className="mobile-nav-drawer"
        closeIcon={<CloseOutlined />}
        title={<img src="/logo.svg" alt="logo" style={{ height: 28 }} />}
      >
        {user && (
          <div className="mobile-nav-user">
            <img className="mobile-nav-user-avatar" src={avatarUrl} alt="" />
            <div className="mobile-nav-user-info">
              <div className="mobile-nav-user-name">{user.name}</div>
              <div className="mobile-nav-user-email">{user.email}</div>
            </div>
          </div>
        )}
        <nav className="mobile-nav-list">
          {drawerItems.map((item) => (
            <div
              key={item.key}
              className={`mobile-nav-item ${selectedKey === item.key ? 'active' : ''}`}
              onClick={() => handleDrawerNav(item.path)}
            >
              <ReactSVG className="icon" src={item.icon} />
              <span>{item.label}</span>
            </div>
          ))}
        </nav>
        <div className="mobile-nav-footer">
          <LanguageSwitcher compact />
          <div className="mobile-nav-item" onClick={() => handleDrawerNav('/logout')}>
            <span>{tc('logOut')}</span>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default MainMenu;
